import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { Card, Pagination } from 'antd';
import WorklogDrawer from '../components/worklog/WorklogDrawer';
import WorklogFilters from '../components/worklog/WorklogFilters';
import WorklogList from '../components/worklog/WorklogList';
import type { WorklogFilterState, WorklogPageProps, WorklogSummary } from '../types/worklog';

const Worklog: React.FC<WorklogPageProps> = ({ worklogs, total, filters }) => {
  const [selected, setSelected] = useState<WorklogSummary | null>(null);

  // 筛选与翻页一律走服务端：条件塞进 query string 后局部刷新 props。
  // 好处是刷新页面、分享链接都能还原当前视图，前端不留第二份筛选状态。
  const navigate = (next: Partial<WorklogFilterState>) => {
    const merged = { ...filters, ...next };
    const params: Record<string, string> = { limit: String(merged.limit) };

    if (merged.task_type) params.task_type = merged.task_type;
    if (merged.working_directory) params.working_directory = merged.working_directory;
    if (merged.search) params.search = merged.search;
    if (merged.since) params.since = merged.since;
    if (merged.until) params.until = merged.until;
    if (merged.offset > 0) params.offset = String(merged.offset);

    router.get('/worklog', params, {
      only: ['worklogs', 'total', 'filters'],
      preserveState: true,
      preserveScroll: true,
    });
  };

  // 改筛选条件时回到第一页 —— 停在第 3 页很可能直接空屏。
  const handleFilterChange = (next: Partial<WorklogFilterState>) =>
    navigate({ ...next, offset: 0 });

  const handlePageChange = (page: number, pageSize: number) =>
    navigate({ limit: pageSize, offset: (page - 1) * pageSize });

  const currentPage = Math.floor(filters.offset / filters.limit) + 1;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">工作日志</h1>
          <p className="text-sm text-gray-500">AI 完成任务后写入的记录，共 {total} 条</p>
        </div>
        {/* 入口只放这里，不占导航栏的位子——一个子系统占两个导航项太重。 */}
        <Link href="/developers" className="text-sm text-blue-600 whitespace-nowrap">
          集成说明
        </Link>
      </div>

      <Card>
        <WorklogFilters filters={filters} onChange={handleFilterChange} />

        <WorklogList worklogs={worklogs} onOpen={setSelected} />

        {total > filters.limit && (
          <div className="mt-4 flex justify-end">
            <Pagination
              current={currentPage}
              pageSize={filters.limit}
              total={total}
              showSizeChanger={false}
              onChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      <WorklogDrawer
        worklog={selected}
        onClose={() => setSelected(null)}
        onDeleted={() => {
          setSelected(null);
          // 删完刷新列表与计数，不然被删的那条还在页面上。
          router.reload({ only: ['worklogs', 'total', 'filters'] });
        }}
      />
    </div>
  );
};

export default Worklog;
